<p-toast></p-toast>

<div class="fadein animation-duration-200">
    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    @if (workspaceWarnings().length) {
        <div class="surface-card border-1 border-orange-200 border-round p-3 mt-3 text-sm text-orange-700 bg-orange-50">
            <i class="pi pi-exclamation-triangle mr-2"></i>{{ workspaceWarnings().join(' ') }}
        </div>
    }

    <div class="grid mt-4">
        <div class="col-12">
            <app-page-data-panel
                title="Linked Shipment & Delivery Tracking"
                subtitle="Shipments and deliveries linked to this execution's order releases, including warehouse trips."
                [resultText]="trackingLinks().length + ' linked shipments / deliveries'">

                <div class="tracking-toolbar">
                    <div class="tracking-toolbar-field">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-truck"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select
                                    inputId="trackingDelivery"
                                    [options]="trackingLinkOptions()"
                                    [ngModel]="selectedTrackingKey()"
                                    (ngModelChange)="onTrackingLinkChange($event)"
                                    optionLabel="label"
                                    optionValue="value"
                                    appendTo="body"
                                    styleClass="w-full">
                                </p-select>
                                <label for="trackingDelivery">Selected truck / delivery</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>

                    <div class="tracking-actions">
                        <p-tag [value]="gpsPollingEnabled ? 'Polling' : 'Paused'" [severity]="gpsPollingEnabled ? 'success' : 'warning'"></p-tag>
                        <p-tag [value]="liveLinkedTrucks() + ' live'" severity="info"></p-tag>
                    </div>
                </div>

                <p-table
                    [value]="trackingLinks()"
                    [loading]="isLoading()"
                    [paginator]="true"
                    [rows]="10"
                    [rowsPerPageOptions]="[10]"
                    styleClass="p-datatable-sm no-borders"
                    responsiveLayout="scroll">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Truck</th>
                            <th>Delivery</th>
                            <th>Order Release</th>
                            <th>Planned Orders</th>
                            <th>Delivery Status</th>
                            <th>Live</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-link>
                        <tr [class.current-gps-row]="link.key === selectedTrackingKey()" (click)="onTrackingLinkChange(link.key)">
                            <td>
                                <div class="font-bold text-900">{{ link.vehicleLabel || 'Unassigned truck' }}</div>
                                <div class="text-xs text-500 mt-1">{{ link.driverName || 'No driver assigned' }}</div>
                            </td>
                            <td>
                                <div class="font-bold text-900">{{ link.deliveryNumber || (link.deliveryId ? 'Delivery #' + link.deliveryId : 'Delivery pending') }}</div>
                                <div class="text-xs text-500 mt-1">{{ link.shipmentNumber || ('Shipment #' + (link.shipmentId || '-')) }}</div>
                                <div class="text-xs text-500 mt-1">{{ trackingLinkStoreSummary(link) }}</div>
                            </td>
                            <td>
                                <div class="font-medium text-900">{{ trackingLinkReleaseSummary(link) }}</div>
                                <div class="text-xs text-500 mt-1">{{ link.releaseStatuses.join(', ') || 'Pending' }}</div>
                                <div class="text-xs text-500 mt-1">{{ link.tripTypes.join(', ') }}</div>
                            </td>
                            <td>
                                <div class="font-bold text-900">{{ link.schedules.length ? link.schedules.length + ' link(s)' : '-' }}</div>
                                <div class="text-xs text-500 mt-1">{{ link.schedules[0]?.scheduleNumber || link.schedules[0]?.storeNumber || '-' }}</div>
                            </td>
                            <td>
                                <p-tag [value]="formatStatusLabel(link.deliveryStatus || link.transportStatus || 'PENDING')" [severity]="progressSeverity(link.deliveryStatus || link.transportStatus)"></p-tag>
                                <div class="text-xs text-500 mt-1">{{ formatStatusLabel(link.shipmentStatus || 'UNKNOWN') }}</div>
                            </td>
                            <td>
                                @if (trackingLinkLive(link); as live) {
                                    <p-tag [value]="gpsStatusLabel(live.liveStatus)" [severity]="gpsStatusSeverity(live.liveStatus)"></p-tag>
                                    <div class="text-xs text-500 mt-1">{{ gpsCoordinateLabel(live.location) }}</div>
                                } @else {
                                    <span class="text-500">No live feed</span>
                                }
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="6" class="text-center p-4 text-500">{{ trackingLoadFailed() ? 'Linked shipments and deliveries could not be loaded. Refresh to try again.' : 'No shipments or deliveries are linked to this execution’s order releases yet.' }}</td>
                        </tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </div>

        <div class="col-12 xl:col-8">
            <app-page-data-panel
                title="Tracking Map"
                subtitle="Live vehicle location, GPS source, replay controls, route history, events, and coordinates for the selected linked delivery."
                [resultText]="selectedTrackingLink()?.deliveryNumber || 'No delivery selected'">

                @if (selectedTrackingLink(); as link) {
                    <div class="tracking-filter-grid">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-database"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <p-select
                                    inputId="deliveryGpsSource"
                                    [options]="gpsSourceOptions()"
                                    [(ngModel)]="selectedGpsSourceKey"
                                    optionLabel="label"
                                    optionValue="value"
                                    [showClear]="true"
                                    appendTo="body"
                                    styleClass="w-full"
                                    (onChange)="onGpsSourceChange()">
                                </p-select>
                                <label for="deliveryGpsSource">GPS source</label>
                            </p-iftalabel>
                        </p-inputGroup>

                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                            <p-iftalabel>
                                <input id="deliveryGpsRange" class="p-inputtext p-component w-full" [value]="gpsHistoryWindowLabel()" readonly />
                                <label for="deliveryGpsRange">Delivery GPS window</label>
                            </p-iftalabel>
                        </p-inputGroup>
                    </div>

                    <section class="delivery-tracking-grid">
                        <div class="tracking-map-panel">
                            <div class="map-status-strip">
                                <span><i class="pi pi-truck"></i>{{ link.vehicleLabel || 'Unassigned truck' }}</span>
                                <span><i class="pi pi-send"></i>{{ link.deliveryNumber || ('Delivery #' + (link.deliveryId || '-')) }}</span>
                                <span><i class="pi pi-database"></i>{{ gpsSourceName(selectedGpsSourceKey || gpsLive()?.sourceKey) }}</span>
                                <span><i class="pi pi-map-marker"></i>{{ gpsHistory().length }} points</span>
                            </div>

                            <google-map height="620px" width="100%" [zoom]="gpsMapZoom" [center]="gpsMapCenter || pickupLocation" [options]="options">
                                <map-polyline [path]="gpsRoutePath()" [options]="{ strokeColor: '#0f766e', strokeOpacity: 0.92, strokeWeight: 5 }"></map-polyline>

                                @if (pickupLocation) {
                                    <map-marker [position]="pickupLocation" title="Pickup" [options]="{ label: 'P', zIndex: 18 }"></map-marker>
                                }
                                @if (dropoffLocation) {
                                    <map-marker [position]="dropoffLocation" title="Dropoff" [options]="{ label: 'D', zIndex: 18 }"></map-marker>
                                }
                                @if (gpsHistory().length) {
                                    @if (gpsPointPosition(gpsHistory()[0]); as startPosition) {
                                        <map-marker [position]="startPosition" title="GPS start" [options]="{ label: 'S', zIndex: 22 }"></map-marker>
                                    }
                                    @if (gpsPointPosition(gpsHistory()[gpsHistory().length - 1]); as endPosition) {
                                        <map-marker [position]="endPosition" title="GPS end" [options]="{ label: 'E', zIndex: 23 }"></map-marker>
                                    }
                                }
                                @if (replayMarkerPosition(); as replayPosition) {
                                    <map-marker [position]="replayPosition" [options]="gpsVehicleMarkerOptions(true)" title="Replay vehicle"></map-marker>
                                } @else {
                                    @if (gpsLiveMarkerPosition(); as livePosition) {
                                        <map-marker [position]="livePosition" [options]="gpsVehicleMarkerOptions(true)" title="Live vehicle"></map-marker>
                                    }
                                }
                            </google-map>
                        </div>

                        <aside class="tracking-side-panel">
                            <div class="tracking-status-card">
                                <div>
                                    <span>Live Status</span>
                                    <strong>{{ gpsStatusLabel(gpsLive()?.liveStatus || gpsHistoryWindow()?.status) }}</strong>
                                </div>
                                <p-tag
                                    [value]="gpsStatusLabel(gpsLive()?.liveStatus || gpsHistoryWindow()?.status)"
                                    [severity]="gpsStatusSeverity(gpsLive()?.liveStatus || gpsHistoryWindow()?.status)">
                                </p-tag>
                            </div>

                            <div class="tracking-kv-grid">
                                <div><span>Vehicle</span><strong>{{ link.vehicleLabel || '-' }}</strong></div>
                                <div><span>Driver</span><strong>{{ link.driverName || '-' }}</strong></div>
                                <div><span>Coordinates</span><strong>{{ gpsCoordinateLabel(gpsDisplayLocation()) }}</strong></div>
                                <div><span>Speed</span><strong>{{ gpsSpeedLabel(gpsDisplayLocation()) }}</strong></div>
                                <div><span>Recorded</span><strong>{{ gpsDateLabel(gpsDisplayLocation()?.recordedAt) }}</strong></div>
                                <div><span>ETA</span><strong>{{ liveTrackingEta() || 'Unavailable' }}</strong></div>
                                <div><span>Distance</span><strong>{{ liveTrackingDistance() || 'Unavailable' }}</strong></div>
                                <div><span>Current Phase</span><strong>{{ currentPhaseLabel() }}</strong></div>
                            </div>

                            <div class="replay-control-card">
                                <div class="replay-heading">
                                    <div>
                                        <span>Route Replay</span>
                                        <strong>{{ replayProgress() }}%</strong>
                                    </div>
                                    <small>{{ replayIndex + 1 }} / {{ replayMaxIndex() + 1 }}</small>
                                </div>

                                <div class="replay-buttons">
                                    <p-button
                                        [label]="replayPlaying ? 'Pause' : 'Play'"
                                        [icon]="replayPlaying ? 'pi pi-pause' : 'pi pi-play'"
                                        [disabled]="!gpsHistory().length"
                                        (onClick)="replayPlaying ? pauseReplay() : startReplay()">
                                    </p-button>
                                    <p-button label="Reset" icon="pi pi-refresh" severity="secondary" [outlined]="true" [disabled]="!gpsHistory().length" (onClick)="resetReplay()"></p-button>
                                </div>

                                <input type="range" [min]="0" [max]="replayMaxIndex()" [value]="replayIndex" (input)="onReplayIndexChange($any($event.target).value)">

                                <p-inputGroup>
                                    <p-inputGroupAddon><i class="pi pi-bolt"></i></p-inputGroupAddon>
                                    <p-iftalabel>
                                        <p-select
                                            inputId="replaySpeed"
                                            [options]="replaySpeeds"
                                            [(ngModel)]="replaySpeed"
                                            optionLabel="label"
                                            optionValue="value"
                                            appendTo="body"
                                            styleClass="w-full"
                                            (onChange)="onReplaySpeedChange()">
                                        </p-select>
                                        <label for="replaySpeed">Replay speed</label>
                                    </p-iftalabel>
                                </p-inputGroup>
                            </div>
                        </aside>
                    </section>

                    <section class="tracking-tables-grid mt-3">
                        <div class="tracking-table-card">
                            <div class="section-toolbar">
                                <div>
                                    <h3>Coordinates</h3>
                                    <p>Route history points returned for the selected truck.</p>
                                </div>
                                <p-tag [value]="gpsHistory().length + ' points'" severity="info"></p-tag>
                            </div>

                            <p-table [value]="gpsHistory()" styleClass="p-datatable-sm no-borders workspace-table" responsiveLayout="scroll">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th>Recorded</th>
                                        <th>Coordinates</th>
                                        <th>Speed</th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-point let-rowIndex="rowIndex">
                                    <tr [class.current-gps-row]="rowIndex === replayIndex && replayPlaying">
                                        <td>{{ gpsDateLabel(point.recordedAt) }}</td>
                                        <td>{{ gpsCoordinateLabel(point) }}</td>
                                        <td>{{ gpsSpeedLabel(point) }}</td>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="emptymessage">
                                    <tr>
                                        <td colspan="3" class="text-center p-4 text-500">No GPS history was recorded in this delivery's workflow window.</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        </div>

                        <div class="tracking-table-card">
                            <div class="section-toolbar">
                                <div>
                                    <h3>Events</h3>
                                    <p>Vehicle event feed returned by the selected GPS source.</p>
                                </div>
                                <p-tag [value]="gpsEvents().length + ' events'" severity="info"></p-tag>
                            </div>

                            <p-table [value]="gpsEvents()" styleClass="p-datatable-sm no-borders workspace-table" responsiveLayout="scroll">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th>Type</th>
                                        <th>Recorded</th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-event>
                                    <tr>
                                        <td>
                                            <p-tag [value]="formatStatusLabel(event.eventType)" [severity]="gpsEventSeverity(event.eventType)"></p-tag>
                                            <div class="text-xs text-500 mt-1">{{ gpsCoordinateLabel(event) }}</div>
                                        </td>
                                        <td>{{ gpsDateLabel(event.eventTime) }}</td>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="emptymessage">
                                    <tr>
                                        <td colspan="2" class="text-center p-4 text-500">No GPS events were returned for this truck.</td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        </div>

                        <div class="tracking-table-card">
                            <div class="section-toolbar">
                                <div>
                                    <h3>Derived Alerts</h3>
                                    <p>Transport and telemetry exceptions from the selected delivery.</p>
                                </div>
                            </div>

                            @if (alerts().length || derivedGpsAlerts().length) {
                                <div class="derived-alert-list">
                                    @for (alert of alerts(); track alert) {
                                        <div>
                                            <span>Transport</span>
                                            <strong>{{ alert }}</strong>
                                            <p-tag value="Alert" severity="warn"></p-tag>
                                        </div>
                                    }
                                    @for (event of derivedGpsAlerts(); track event.id) {
                                        <div>
                                            <span>{{ gpsDateLabel(event.eventTime) }}</span>
                                            <strong>{{ formatStatusLabel(event.eventType) }}</strong>
                                            <p-tag [value]="gpsEventSeverity(event.eventType)" [severity]="gpsEventSeverity(event.eventType)"></p-tag>
                                        </div>
                                    }
                                </div>
                            } @else {
                                <div class="inline-empty">No derived tracking alerts for the selected delivery.</div>
                            }
                        </div>
                    </section>
                } @else {
                    <div class="empty-state">
                        <i class="pi pi-map"></i>
                        <strong>No linked delivery selected</strong>
                        <span>Select a linked truck or wait until POSM planned orders are connected to deliveries.</span>
                    </div>
                }
            </app-page-data-panel>
        </div>

        <div class="col-12 xl:col-4">
            <app-page-data-panel
                title="Delivery Status Updates"
                subtitle="Operational phase, workflow updates, order releases, and POSM order links for the selected truck."
                [resultText]="deliveryStatusUpdates().length + ' updates'">

                @if (selectedTrackingLink(); as link) {
                    <div class="status-chain-card">
                        <div class="status-chain-step">
                            <span>Truck</span>
                            <strong>{{ link.vehicleLabel || 'Unassigned truck' }}</strong>
                            <small>{{ link.driverName || 'No driver assigned' }}</small>
                        </div>
                        <div class="status-chain-arrow">→</div>
                        <div class="status-chain-step">
                            <span>Delivery</span>
                            <strong>{{ link.deliveryNumber || ('Delivery #' + (link.deliveryId || '-')) }}</strong>
                            <small>{{ formatStatusLabel(link.deliveryStatus || 'PENDING') }}</small>
                        </div>
                        <div class="status-chain-arrow">→</div>
                        <div class="status-chain-step">
                            <span>Order Release</span>
                            <strong>{{ trackingLinkReleaseSummary(link) }}</strong>
                            <small>{{ link.releaseStatuses.join(', ') || 'Pending' }}</small>
                        </div>
                        <div class="status-chain-arrow">→</div>
                        <div class="status-chain-step">
                            <span>POSM Links</span>
                            <strong>{{ link.schedules.length }} planned order(s)</strong>
                            <small>{{ trackingLinkStoreSummary(link) }}</small>
                        </div>
                    </div>

                    <div class="status-level-grid mt-3">
                        <div>
                            <span>Shipment Status</span>
                            <strong>{{ formatStatusLabel(shipmentWorkflowState()) }}</strong>
                        </div>
                        <div>
                            <span>Delivery Status</span>
                            <strong>{{ formatStatusLabel(deliveryWorkflowState()) }}</strong>
                        </div>
                        <div>
                            <span>Site Status</span>
                            <strong>{{ activeOrNextStopLabel() }}</strong>
                        </div>
                        <div>
                            <span>Current Phase</span>
                            <strong>{{ currentPhaseLabel() }}</strong>
                        </div>
                    </div>

                    <div class="stop-progress-grid mt-3">
                        <div>
                            <span>Pickup Progress</span>
                            <strong>{{ pickupProgressLabel() }}</strong>
                        </div>
                        <div>
                            <span>Dropoff Progress</span>
                            <strong>{{ dropoffProgressLabel() }}</strong>
                        </div>
                    </div>

                    <div class="status-card-grid mt-3">
                        <section class="tracking-table-card p-3">
                            <div class="section-toolbar">
                                <div>
                                    <h3>Order Releases</h3>
                                    <p>Delivery release rows tied back to this truck.</p>
                                </div>
                            </div>

                            @if (selectedOperationStatus()?.orderReleases?.length) {
                                <div class="release-list">
                                    @for (release of selectedOperationStatus()?.orderReleases || []; track release.releaseId || release.releaseNumber) {
                                        <div class="release-item">
                                            <div>
                                                <strong>{{ release.releaseNumber || release.sourceNumber || ('Release #' + (release.releaseId || '-')) }}</strong>
                                                <small>{{ release.sourceType || 'Order release' }}{{ release.sourceNumber ? (' | ' + release.sourceNumber) : '' }}</small>
                                            </div>
                                            <p-tag [value]="formatStatusLabel(releaseOrderStatus(release))" [severity]="orderReleaseSeverity(release)"></p-tag>
                                        </div>
                                    }
                                </div>
                            } @else {
                                <div class="inline-empty">No shipment workflow order releases were returned for this delivery.</div>
                            }
                        </section>

                        <section class="tracking-table-card p-3">
                            <div class="section-toolbar">
                                <div>
                                    <h3>POSM Planned Orders</h3>
                                    <p>Delivery schedule rows linked to the selected truck.</p>
                                </div>
                            </div>

                            @if (selectedReleaseGroups().length) {
                                <div class="schedule-link-list">
                                    @for (release of selectedReleaseGroups(); track release.key) {
                                        <div class="schedule-release-group">
                                            <div class="schedule-release-header">
                                                <div>
                                                    <strong>{{ release.releaseNumber }}</strong>
                                                    <small>{{ release.schedules.length }} POSM link(s)</small>
                                                </div>
                                                <p-tag [value]="formatStatusLabel(release.releaseStatus)" [severity]="transportSeverity(release.releaseStatus)"></p-tag>
                                            </div>
                                            @for (schedule of release.schedules; track schedule.id || schedule.scheduleNumber) {
                                                <div class="schedule-link-item">
                                                    <div>
                                                        <strong>{{ schedule.scheduleNumber || ('Line #' + (schedule.id || '-')) }}</strong>
                                                        <small>{{ scheduleStoreName(schedule) }}{{ schedule.standName ? (' | ' + schedule.standName) : '' }}</small>
                                                    </div>
                                                    <span>{{ schedule.plannedInstallationDate || schedule.installationDate | date:'mediumDate' }}</span>
                                                </div>
                                            }
                                        </div>
                                    }
                                </div>
                            } @else {
                                <div class="inline-empty">No POSM delivery schedule links are tied to the selected truck.</div>
                            }
                        </section>
                    </div>

                    <section class="tracking-table-card p-3 mt-3">
                        <div class="section-toolbar">
                            <div>
                                <h3>Status Updates</h3>
                                <p>Timeline events and delivery status history for the selected linked delivery.</p>
                            </div>
                        </div>

                        @if (deliveryStatusUpdates().length) {
                            <div class="status-update-list">
                                @for (update of deliveryStatusUpdates(); track update.id) {
                                    <div class="status-update-item">
                                        <div>
                                            <strong>{{ update.title }}</strong>
                                            <small>{{ update.subtitle }}</small>
                                        </div>
                                        <div class="status-update-meta">
                                            <p-tag [value]="update.source === 'TIMELINE' ? 'Timeline' : 'Status'" [severity]="update.source === 'TIMELINE' ? 'info' : 'success'"></p-tag>
                                            <span>{{ gpsDateLabel(update.timestamp) }}</span>
                                        </div>
                                    </div>
                                }
                            </div>
                        } @else {
                            <div class="inline-empty">No delivery status updates were returned for the selected truck.</div>
                        }
                    </section>
                } @else {
                    <div class="empty-state">
                        <i class="pi pi-list-check"></i>
                        <strong>No delivery context</strong>
                        <span>Select a linked truck to view delivery status updates and release linkage.</span>
                    </div>
                }
            </app-page-data-panel>
        </div>

        <div class="col-12 lg:col-6">
            <app-page-data-panel
                title="Shipment Files"
                subtitle="Waybills, POD, photos, and other attachments pulled from POSM-linked shipments."
                [resultText]="linkedFiles().length + ' files'">

                <p-table [value]="linkedFiles()" [loading]="isLoading()" styleClass="p-datatable-sm no-borders" responsiveLayout="scroll">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>File</th>
                            <th>Source</th>
                            <th>Store</th>
                            <th>Type</th>
                            <th class="text-right">Open</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-file>
                        <tr>
                            <td>
                                <div class="font-bold text-900">{{ file.fileName || 'Attachment' }}</div>
                                <div class="text-xs text-500 mt-1">{{ file.creationDate | date:'medium' }}</div>
                            </td>
                            <td>
                                <div class="font-medium text-900">{{ fileSourceLabel(file) }}</div>
                                <div class="text-xs text-500 mt-1">{{ file.deliveryNumber || file.shipmentNumber || '-' }}</div>
                            </td>
                            <td>
                                <div>{{ file.storeName || '-' }}</div>
                                <div class="text-xs text-500 mt-1">{{ file.city || '-' }}</div>
                            </td>
                            <td>
                                <p-tag [value]="file.sourceType" [severity]="linkedFileSeverity(file.fileType)"></p-tag>
                                <div class="text-xs text-500 mt-1">{{ file.fileType || '-' }}</div>
                            </td>
                            <td class="text-right">
                                <p-button icon="pi pi-external-link" [rounded]="true" [text]="true" [disabled]="!file.fileUrl" (onClick)="openUrl(file.fileUrl)"></p-button>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="5" class="text-center p-4 text-500">No shipment files are linked to these POSM planned orders yet.</td>
                        </tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </div>

        <div class="col-12 lg:col-6">
            <app-page-data-panel title="Delivery Notes" subtitle="Generated from task, store, item, and signature data" [resultText]="deliveryNotes().length + ' notes'">
                <p-table [value]="deliveryNotes()" [loading]="isLoading()" styleClass="p-datatable-sm no-borders" responsiveLayout="scroll">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>DN</th>
                            <th>Store</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th class="text-right">PDF</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-note>
                        <tr>
                            <td>
                                <div class="font-bold text-900">{{ note.dnNumber || ('DN #' + note.id) }}</div>
                                <div class="text-xs text-500 mt-1">{{ note.jobNumber || '-' }}</div>
                            </td>
                            <td>
                                <div>{{ note.storeName || '-' }}</div>
                                <div class="text-xs text-500 mt-1">{{ note.city || note.storeNumber || '-' }}</div>
                            </td>
                            <td>{{ note.dnType || '-' }}</td>
                            <td><p-tag [value]="note.status || 'GENERATED'" [severity]="note.status === 'SIGNED' ? 'success' : 'info'"></p-tag></td>
                            <td class="text-right">
                                <p-button icon="pi pi-external-link" [rounded]="true" [text]="true" [disabled]="!(note.generatedPdfUrl || note.pdfUrl)" (onClick)="openUrl(note.generatedPdfUrl || note.pdfUrl)"></p-button>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="5" class="text-center p-4 text-500">No delivery notes generated yet.</td>
                        </tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </div>

        <div class="col-12">
            <app-page-data-panel title="Store Change Requests" subtitle="Store changes require OPS and Sales approval before applying" [resultText]="storeChanges().length + ' requests'">
                @if (storeChangesWarning()) {
                    <div class="surface-card border-1 border-orange-200 border-round p-3 mb-3 text-sm text-orange-700 bg-orange-50">
                        <i class="pi pi-exclamation-triangle mr-2"></i>{{ storeChangesWarning() }}
                    </div>
                }


                <p-table [value]="storeChanges()" [loading]="isLoading()" styleClass="p-datatable-sm no-borders" responsiveLayout="scroll">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Store</th>
                            <th>Requested Change</th>
                            <th>OPS</th>
                            <th>Sales</th>
                            <th class="text-right">Actions</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-change>
                        <tr>
                            <td>
                                <div class="font-bold text-900">{{ change.currentStoreNumber || ('Store #' + change.storeId) }}</div>
                                <p-tag [value]="change.status || 'PENDING_APPROVAL'" [severity]="changeSeverity(change.status)" styleClass="mt-1"></p-tag>
                            </td>
                            <td>
                                <div>{{ change.requestedCity || change.currentCity || '-' }}</div>
                                <div class="text-xs text-500 mt-1">{{ change.reason || '-' }}</div>
                            </td>
                            <td><p-tag [value]="change.opsApprovalStatus || 'PENDING'" [severity]="approvalSeverity(change.opsApprovalStatus)"></p-tag></td>
                            <td><p-tag [value]="change.salesApprovalStatus || 'PENDING'" [severity]="approvalSeverity(change.salesApprovalStatus)"></p-tag></td>
                            <td class="text-right">
                                @if (change.status !== 'APPLIED' && change.status !== 'REJECTED') {
                                    <div class="flex justify-content-end gap-1">
                                        <p-button label="OPS" icon="pi pi-check" size="small" [outlined]="true" severity="success" (onClick)="openReviewStoreChange(change, 'OPS', true)"></p-button>
                                        <p-button label="Sales" icon="pi pi-check" size="small" [outlined]="true" severity="success" (onClick)="openReviewStoreChange(change, 'SALES', true)"></p-button>
                                    </div>
                                } @else {
                                    <span class="text-500">-</span>
                                }
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="5" class="text-center p-4 text-500">No store change requests.</td>
                        </tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </div>

    </div>
</div>

<p-dialog header="POSM Task Detail" [visible]="taskDialogVisible()" (visibleChange)="taskDialogVisible.set($event)" [modal]="true" [style]="{ width: '70rem', maxWidth: '95vw' }">
    @if (selectedTask(); as task) {
        <div class="grid">
            <div class="col-12 md:col-4">
                <div class="surface-50 border-round border-1 surface-border p-3 h-full">
                    <div class="text-xs text-500 uppercase mb-2">Target Store</div>
                    <div class="text-lg font-bold text-900">{{ taskStoreName(task) }}</div>
                    <div class="text-sm text-600 mt-2">{{ task.store?.location?.address || task.store?.address || '-' }}</div>
                    <div class="mt-3 flex flex-column gap-2">
                        <div><span class="text-500">City</span><div class="font-medium">{{ task.store?.city || '-' }}</div></div>
                        <div><span class="text-500">Contact</span><div class="font-medium">{{ task.store?.contactPersonName || '-' }} {{ task.store?.contactPersonPhone || '' }}</div></div>
                        <div><span class="text-500">Planned</span><div class="font-medium">{{ task.plannedInstallationDate || task.store?.plannedDate | date:'mediumDate' }}</div></div>
                    </div>
                </div>
            </div>
            <div class="col-12 md:col-8">
                <p-table [value]="task.targetItems || []" styleClass="p-datatable-sm no-borders">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Item Number</th>
                            <th>SKU</th>
                            <th>Item / Stand</th>
                            <th>Size</th>
                            <th>Qty</th>
                            <th>Action</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-line>
                        <tr>
                            <td>{{ line.itemNumber || '-' }}</td>
                            <td>{{ line.skuNumber || '-' }}</td>
                            <td>{{ line.itemName || '-' }}</td>
                            <td>{{ line.itemSize || '-' }}</td>
                            <td>{{ line.quantity || 0 }}</td>
                            <td>{{ line.actionRequired || '-' }}</td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="6" class="text-center p-4 text-500">No item lines linked to this task.</td></tr>
                    </ng-template>
                </p-table>
            </div>
            <div class="col-12">
                <div class="flex flex-wrap gap-2">
                    <p-button label="Add Evidence" icon="pi pi-upload" (onClick)="openEvidenceDialog(task)"></p-button>
                    <p-button label="Complete Task" icon="pi pi-check-circle" severity="success" (onClick)="openCompletionDialog(task)"></p-button>
                    <p-button label="Generate DN" icon="pi pi-file-pdf" severity="secondary" (onClick)="openDeliveryNoteDialog(task)"></p-button>
                    @if (task.delayReason && task.delayApprovalStatus !== 'APPROVED') {
                        <p-button label="Approve Delay" icon="pi pi-check" severity="warn" [outlined]="true" (onClick)="openReviewDelay(task, true)"></p-button>
                        <p-button label="Reject Delay" icon="pi pi-times" severity="danger" [outlined]="true" (onClick)="openReviewDelay(task, false)"></p-button>
                    }
                </div>
            </div>
            <div class="col-12">
                <p-table [value]="selectedTaskEvidence()" styleClass="p-datatable-sm no-borders">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Evidence</th>
                            <th>Uploaded</th>
                            <th>Geo</th>
                            <th>Status</th>
                            <th class="text-right">Actions</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-evidence>
                        <tr>
                            <td>
                                <div class="font-bold text-900">{{ evidence.evidenceType }}</div>
                                <div class="text-xs text-500 mt-1">{{ evidence.fileName || evidence.fileUrl }}</div>
                            </td>
                            <td>{{ evidence.uploadedAt | date:'medium' }}</td>
                            <td>{{ evidence.latitude || '-' }}, {{ evidence.longitude || '-' }}</td>
                            <td><p-tag [value]="evidence.approvalStatus || 'PENDING'" [severity]="evidenceSeverity(evidence.approvalStatus)"></p-tag></td>
                            <td class="text-right">
                                <div class="flex justify-content-end gap-1">
                                    <p-button icon="pi pi-external-link" [rounded]="true" [text]="true" (onClick)="openUrl(evidence.fileUrl)"></p-button>
                                    <p-button icon="pi pi-check" [rounded]="true" [text]="true" severity="success" (onClick)="openReviewEvidence(evidence, true)"></p-button>
                                    <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="danger" (onClick)="openReviewEvidence(evidence, false)"></p-button>
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="5" class="text-center p-4 text-500">No evidence has been uploaded for this task.</td></tr>
                    </ng-template>
                </p-table>
            </div>
        </div>
    }
</p-dialog>

<p-dialog header="Upload Task Evidence" [visible]="evidenceDialogVisible()" (visibleChange)="evidenceDialogVisible.set($event)" [modal]="true" [style]="{ width: '44rem', maxWidth: '95vw' }">
    <div class="grid">
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-tags"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-select inputId="evidenceType" [options]="evidenceTypeOptions" [(ngModel)]="evidenceForm.evidenceType" appendTo="body" styleClass="w-full"></p-select>
                    <label for="evidenceType">Evidence Type</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="evidenceFileName" pInputText [(ngModel)]="evidenceForm.fileName" class="w-full">
                    <label for="evidenceFileName">File Name</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-link"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="evidenceFileUrl" pInputText [(ngModel)]="evidenceForm.fileUrl" class="w-full">
                    <label for="evidenceFileUrl">File URL</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-inputnumber inputId="evidenceLatitude" [(ngModel)]="evidenceForm.latitude" [useGrouping]="false" [maxFractionDigits]="8"></p-inputnumber>
                    <label for="evidenceLatitude">Latitude</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-inputnumber inputId="evidenceLongitude" [(ngModel)]="evidenceForm.longitude" [useGrouping]="false" [maxFractionDigits]="8"></p-inputnumber>
                    <label for="evidenceLongitude">Longitude</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <textarea id="evidenceDescription" pTextarea rows="3" [(ngModel)]="evidenceForm.description" class="w-full"></textarea>
                    <label for="evidenceDescription">Description</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="evidenceDialogVisible.set(false)"></p-button>
        <p-button label="Save Evidence" icon="pi pi-check" (onClick)="submitEvidence()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog header="Complete POSM Task" [visible]="completionDialogVisible()" (visibleChange)="completionDialogVisible.set($event)" [modal]="true" [style]="{ width: '44rem', maxWidth: '95vw' }">
    <div class="grid">
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-inputnumber inputId="completeLatitude" [(ngModel)]="completionForm.latitude" [useGrouping]="false" [maxFractionDigits]="8"></p-inputnumber>
                    <label for="completeLatitude">Latitude</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-inputnumber inputId="completeLongitude" [(ngModel)]="completionForm.longitude" [useGrouping]="false" [maxFractionDigits]="8"></p-inputnumber>
                    <label for="completeLongitude">Longitude</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-camera"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="completePhotoUrl" pInputText [(ngModel)]="completionForm.photoUrl" class="w-full">
                    <label for="completePhotoUrl">Completion Photo URL</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-pencil"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="completeSignatureUrl" pInputText [(ngModel)]="completionForm.signatureUrl" class="w-full">
                    <label for="completeSignatureUrl">Signature URL</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <textarea id="completeRemarks" pTextarea rows="3" [(ngModel)]="completionForm.remarks" class="w-full"></textarea>
                    <label for="completeRemarks">Completion Remarks</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="completionDialogVisible.set(false)"></p-button>
        <p-button label="Submit Completion" icon="pi pi-check" severity="success" (onClick)="submitCompletion()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog header="Generate Delivery Note" [visible]="deliveryNoteDialogVisible()" (visibleChange)="deliveryNoteDialogVisible.set($event)" [modal]="true" [style]="{ width: '38rem', maxWidth: '95vw' }">
    <div class="grid">
        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-select inputId="dnType" [options]="dnTypeOptions" [(ngModel)]="deliveryNoteForm.dnType" appendTo="body" styleClass="w-full"></p-select>
                    <label for="dnType">Delivery Note Type</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-pencil"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="dnSignatureUrl" pInputText [(ngModel)]="deliveryNoteForm.signatureUrl" class="w-full">
                    <label for="dnSignatureUrl">Signature URL</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-datepicker inputId="dnSignedAt" [(ngModel)]="deliveryNoteForm.signedAt" [showTime]="true" hourFormat="24" appendTo="body" styleClass="w-full"></p-datepicker>
                    <label for="dnSignedAt">Signed At</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="deliveryNoteDialogVisible.set(false)"></p-button>
        <p-button label="Generate" icon="pi pi-file-pdf" (onClick)="submitDeliveryNote()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog header="Request Store Change" [visible]="storeChangeDialogVisible()" (visibleChange)="storeChangeDialogVisible.set($event)" [modal]="true" [style]="{ width: '58rem', maxWidth: '95vw' }">
    <div class="grid">
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-select inputId="changeStore" [options]="storeOptions()" [(ngModel)]="storeChangeForm.storeId" appendTo="body" styleClass="w-full"></p-select>
                    <label for="changeStore">Store</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="changeStoreNumber" pInputText [(ngModel)]="storeChangeForm.requestedStoreNumber" class="w-full">
                    <label for="changeStoreNumber">Requested Store Number</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="changeCity" pInputText [(ngModel)]="storeChangeForm.requestedCity" class="w-full">
                    <label for="changeCity">Requested City</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-datepicker inputId="changePlannedDate" [(ngModel)]="storeChangeForm.requestedPlannedDate" appendTo="body" styleClass="w-full"></p-datepicker>
                    <label for="changePlannedDate">Requested Installation Date</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="changeContactName" pInputText [(ngModel)]="storeChangeForm.requestedContactName" class="w-full">
                    <label for="changeContactName">Requested Contact Name</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-phone"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="changeContactPhone" pInputText [(ngModel)]="storeChangeForm.requestedContactPhone" class="w-full">
                    <label for="changeContactPhone">Requested Contact Phone</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-map"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="changeAddress" pInputText [(ngModel)]="storeChangeForm.requestedAddress" class="w-full">
                    <label for="changeAddress">Requested Address</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-inputnumber inputId="changeLatitude" [(ngModel)]="storeChangeForm.requestedLatitude" [useGrouping]="false" [maxFractionDigits]="8"></p-inputnumber>
                    <label for="changeLatitude">Requested Latitude</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12 md:col-6">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <p-inputnumber inputId="changeLongitude" [(ngModel)]="storeChangeForm.requestedLongitude" [useGrouping]="false" [maxFractionDigits]="8"></p-inputnumber>
                    <label for="changeLongitude">Requested Longitude</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
        <div class="col-12">
            <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <textarea id="changeReason" pTextarea rows="3" [(ngModel)]="storeChangeForm.reason" class="w-full"></textarea>
                    <label for="changeReason">Reason</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="storeChangeDialogVisible.set(false)"></p-button>
        <p-button label="Submit Request" icon="pi pi-check" (onClick)="submitStoreChange()"></p-button>
    </ng-template>
</p-dialog>

<p-dialog header="Review Decision" [visible]="reviewDialogVisible()" (visibleChange)="reviewDialogVisible.set($event)" [modal]="true" [style]="{ width: '36rem', maxWidth: '95vw' }">
    <p class="text-600 mt-0">Record the approval decision with an optional comment.</p>
    <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-comment"></i></p-inputGroupAddon>
        <p-iftalabel>
            <textarea id="reviewComment" pTextarea rows="4" [(ngModel)]="reviewForm.comment" class="w-full"></textarea>
            <label for="reviewComment">Review Comment</label>
        </p-iftalabel>
    </p-inputGroup>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="reviewDialogVisible.set(false)"></p-button>
        <p-button label="Save Decision" icon="pi pi-check" (onClick)="submitReview()"></p-button>
    </ng-template>
</p-dialog>
